import { Link } from 'react-router-dom';
import { Card, Skeleton } from '@crestrewards/ui';

/**
 * One headline number with a label and an optional note underneath.
 *
 * An internal piece of the dashboard feature. Deliberately not in
 * `packages/ui`: nothing else uses it yet, and moving it there before a second
 * caller exists would be guessing at an API. If Client Admin's dashboard wants
 * the same tile (PROJECT_SPEC_CLIENT.md §3.2 has a `StatCard.jsx` of its own),
 * that is the moment it graduates to `packages/ui`.
 *
 * When `to` is set the whole tile is a link, so a number that represents work
 * to do takes you to the screen where you do it. `Card` is a plain div with no
 * `asChild` support, so the padding moves onto the inner element rather than
 * the shell — that way the link fills the whole tile and the hover state covers
 * it edge to edge.
 */
export default function StatCard({ label, value, note, tone = 'default', to, loading }) {
  const toneClass =
    {
      default: 'text-foreground',
      warning: 'text-warning',
      danger: 'text-destructive-on-tint',
      success: 'text-success-on-tint',
    }[tone] ?? 'text-foreground';

  const body = (
    <>
      <div className="text-label text-muted-foreground">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-16" />
      ) : (
        <div className={`mt-1 text-2xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
      )}
      {note && !loading ? (
        <div className="mt-1 text-label text-muted-foreground">{note}</div>
      ) : null}
    </>
  );

  if (to && !loading) {
    return (
      <Card className="overflow-hidden">
        <Link
          to={to}
          className="block px-5 py-4 transition-colors hover:bg-surface-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        >
          {body}
        </Link>
      </Card>
    );
  }

  return <Card className="px-5 py-4">{body}</Card>;
}
